import { useSectionAccess } from "../auth/useSectionAccess";
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { TableSkeleton } from "../components/Loading";
import { Pager } from "../components/Pager";
import type { FilterOption } from "../api/admin";

/** Koliko ponuda ide u jednu stranu. Spisak nosi celu istoriju, pa nikad nije ceo. */
const PAGE_SIZE = 50;

import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { offersApi, type Offer } from "../api/offers";
import { offerStagesApi, type OfferStage } from "../api/offerStages";
import { useToast } from "../components/Toast";
import Ic from "../components/icons.jsx";
import type { ApiError } from "../api/client";
import { stageColor, tint } from "./offersBoard";
import { LoadFailed } from "../components/LoadFailed";
import { OffersViewSwitcher } from "../components/offers/OffersViewSwitcher";
import { OffersFilterBar, card, emptyState } from "../components/offers/OffersFilterBar";
import {
  hasActiveFilter, distinctOwners,
  EMPTY_FILTER_CRITERIA, type OfferFilterCriteria,
} from "./offersFilter";
import { localizedName } from "../lib/localizedName";
import { formatMoney } from "../lib/format";
import { usePage } from "../lib/usePage";

const money = (n: number | null | undefined, cur: string | null | undefined) =>
  formatMoney(n, cur);

export default function OffersPage() {
  const { canWrite } = useSectionAccess("offers");
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [offers, setOffers] = useState<Offer[] | null>(null);
  /** Poruka kvara; dok stoji, ekran govori da učitavanje nije uspelo, a ne da ponuda nema. */
  const [greska, setGreska] = useState<string | null>(null);
  const [stages, setStages] = useState<OfferStage[]>([]);
  const [criteria, setCriteria] = useState<OfferFilterCriteria>(() => ({
    ...EMPTY_FILTER_CRITERIA,
    search: searchParams.get("q") ?? "",
  }));
  const [total, setTotal] = useState(0);
  const [page, setPage] = usePage();
  const [countryOptions, setCountryOptions] = useState<FilterOption[]>([]);
  const [busy, setBusy] = useState(false);

  /**
   * Filteri i redosled rade u bazi. Spisak nosi istoriju iz starog sistema — sto hiljada
   * ponuda — pa jedna strana nikad nije ceo spisak, a filtriranje u pregledaču bi filtriralo
   * samo ono što je ta strana slučajno donela.
   */
  async function load(strana = page) {
    setBusy(true);
    try {
      const r = await offersApi.list({
        stageId: criteria.stageId || undefined,
        ownerId: criteria.ownerId || undefined,
        country: criteria.country || undefined,
        dateFrom: criteria.dateFrom || undefined,
        dateTo: criteria.dateTo || undefined,
        q: criteria.search.trim() || undefined,
        page: strana,
        size: PAGE_SIZE,
      });
      setOffers(r.content);
      setTotal(r.totalElements);
      setGreska(null);
    } catch (e) {
      const poruka = (e as ApiError).message;
      toast.error(poruka);
      // Ne `setOffers([])`: prazan spisak i neuspelo učitavanje nisu ista stvar, a obaveštenje
      // nestane za nekoliko sekundi i ostane „Još nema ponuda" nad bazom od sto hiljada ponuda.
      setGreska(poruka);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    offersApi.countries().then(setCountryOptions).catch(() => setCountryOptions([]));
  }, []);

  // Promena kriterijuma vraca na prvu stranu; citanje radi JEDAN efekat ispod, nad kriterijumima i
  // stranom zajedno. Dva odvojena efekta su se sudarala: onaj za stranu je preskakao nulu da ne bi
  // dvaput ucitao pri promeni filtera, pa povratak sa druge strane na prvu nije ucitavao nista —
  // tabela je ostajala na staroj strani dok je stranicar pokazivao prvu.
  useEffect(() => {
    setPage(0);
  }, [criteria.stageId, criteria.ownerId, criteria.country, criteria.dateFrom, criteria.dateTo, criteria.search]);

  // Odlaganje stoji samo zbog otkucanog slova; promena strane ili izbor iz spiska ide odmah.
  useEffect(() => {
    const id = setTimeout(() => void load(page), criteria.search ? 450 : 0);
    return () => clearTimeout(id);
  }, [criteria.stageId, criteria.ownerId, criteria.country, criteria.dateFrom, criteria.dateTo, criteria.search, page]);

  useEffect(() => {
    offerStagesApi.list().then(setStages).catch(() => setStages([]));
  }, []);

  // `q` (search) is the one criterion shared across board/list/funnel via the
  // URL, so switching views doesn't lose what the user typed.
  const setSearch = (v: string) => {
    setCriteria((c) => ({ ...c, search: v }));
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (v) next.set("q", v); else next.delete("q");
      return next;
    }, { replace: true });
  };

  // Države dolaze sa servera: iz učitane strane bi se videle samo one koje je ona donela.
  const countries = useMemo(() => countryOptions.map((o) => o.value), [countryOptions]);
  const owners = useMemo(() => distinctOwners(offers ?? []), [offers]);
  const showOwnerFilter = owners.length > 1;
  // Baza je već filtrirala; ono što je stiglo jeste odgovor.
  const filtered = offers ?? [];
  const filtersActive = hasActiveFilter(criteria);

  const setDateFrom = (v: string) => { setCriteria((c) => ({ ...c, dateFrom: v })); };
  const setDateTo = (v: string) => { setCriteria((c) => ({ ...c, dateTo: v })); };
  const clearFilters = () => {
    setCriteria({ ...EMPTY_FILTER_CRITERIA, search: criteria.search });
  };

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <nav className="crumbs" aria-label="Breadcrumb">
            <span>{t("offers.crumb.commercial")}</span>
            <span>{t("offers.list.title")}</span>
          </nav>
          <h1>{t("offers.list.title")}</h1>
          <div className="sub">{t("offers.list.subtitle")}</div>
        </div>
        <div className="page-actions">
          {canWrite && <Link to="/offers/new" className="btn primary"><Ic.plus /> {t("offers.list.new")}</Link>}
        </div>
      </div>

      <OffersViewSwitcher active="list" search={criteria.search} />
      <Pager page={page} size={PAGE_SIZE} total={total} busy={busy} onPage={setPage} />

      <OffersFilterBar
        criteria={criteria}
        onSearchChange={setSearch}
        onStageChange={(v) => setCriteria((c) => ({ ...c, stageId: v }))}
        onCountryChange={(v) => setCriteria((c) => ({ ...c, country: v }))}
        onOwnerChange={(v) => setCriteria((c) => ({ ...c, ownerId: v }))}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        stages={stages}
        countries={countries}
        owners={owners}
        showOwnerFilter={showOwnerFilter}
        shownCount={filtered.length}
        totalCount={offers?.length ?? 0}
        filtersActive={filtersActive}
        onClear={clearFilters}
        showSummary={offers != null && offers.length > 0}
      />

      <section style={card}>
        {greska ? (
          <LoadFailed message={greska} onRetry={() => void load()} />
        ) : offers == null ? (
          <TableSkeleton rows={8} cols={6} label={t("common.loading")} />
        ) : offers.length === 0 ? (
          <div style={emptyState}>
            <p style={{ margin: 0, color: "var(--ink-3)" }}>{t("offers.list.empty.title")}</p>
            <p style={{ margin: "6px 0 0", color: "var(--ink-4)", fontSize: 12 }}>{t("offers.list.empty.hint")}</p>
            {canWrite && <Link to="/offers/new" className="btn primary" style={{ marginTop: 14 }}><Ic.plus /> {t("offers.list.new")}</Link>}
          </div>
        ) : filtered.length === 0 ? (
          <div style={emptyState}>
            <p style={{ margin: 0, color: "var(--ink-3)" }}>{t("offers.filter.empty")}</p>
            <button type="button" className="btn sm" onClick={clearFilters} style={{ marginTop: 14 }}>{t("offers.filter.clear")}</button>
          </div>
        ) : (
          <>
          <table className="tbl" style={tbl}>
            <thead>
              <tr>
                <th style={th}>{t("offers.col.number")}</th>
                <th style={th}>{t("offers.col.customer")}</th>
                <th style={th}>{t("offers.col.date")}</th>
                <th style={th}>{t("offers.col.stage")}</th>
                <th style={{ ...th, textAlign: "right" }}>{t("offers.col.total")}</th>
                <th style={th}>{t("offers.col.owner")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => {
                const color = o.stage ? stageColor(o.stage) : null;
                return (
                  <tr key={o.id} style={{ cursor: "pointer" }} className="row-link" onClick={() => navigate(`/offers/${o.id}`)}>
                    <td style={td}><span style={mono}>{o.offerNumber}</span></td>
                    <td style={td}>{o.customerName}</td>
                    <td style={td}>{o.offerDate}</td>
                    <td style={td}>
                      {o.stage && color ? (
                        <span style={{ ...statusPill, color, background: tint(color) }}>{localizedName(o.stage, i18n.resolvedLanguage ?? "en")}</span>
                      ) : "—"}
                    </td>
                    <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{money(o.displayAmount, o.currency)}</td>
                    <td style={{ ...td, color: "var(--ink-3)" }}>{o.ownerName ?? "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pager page={page} size={PAGE_SIZE} total={total} busy={busy} onPage={setPage} />
          </>
        )}
      </section>

      <style>{`.row-link:hover td{background:var(--surface-2)}`}</style>
    </div>
  );
}

const tbl: CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const th: CSSProperties = {
  textAlign: "left", padding: "10px 8px", color: "var(--ink-3)", fontWeight: 600,
  borderBottom: "1px solid var(--border)", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4,
};
const td: CSSProperties = { padding: "10px 8px", borderBottom: "1px solid var(--border)", verticalAlign: "middle" };
const mono: CSSProperties = { fontFamily: "var(--mono)", fontSize: 12, color: "var(--ink-2)" };
const statusPill: CSSProperties = { fontSize: 12, fontWeight: 600, padding: "2px 10px", borderRadius: 999 };
